import { GanttSelectionState } from '../../store/atom';

import { Task } from '@/features/saveSite/types';
import { DataFile, DataFileType, TransferStatusCode } from '@/features/siteTop/types';

export enum GanttSelectionType {
  date = 'date',
  cross = 'cross',
}

export type TransformedGanttSelection = {
  allCrossedTasks: Task[];

  designDataFiles: DataFile[];
  asBuiltDataFile?: DataFile;
  task?: Task;

  selectionType?: GanttSelectionType;
  anySelected?: boolean;
};

const initialTransformedGanttSelection: TransformedGanttSelection = {
  allCrossedTasks: [],
  designDataFiles: [],
  anySelected: false,
};

/**
 * GanttSelectionを、扱いやすい形に変換する
 * @param input
 * @param dataFiles
 */
export const transformGanttSelection = (
  input: GanttSelectionState | null,
  allDataFiles: DataFile[],
  allTasks: Task[]
): TransformedGanttSelection => {
  if (!input) {
    return initialTransformedGanttSelection;
  }
  //date、crossesのどちらかが選択されているか
  const anySelected = Boolean(input.date || input.crosses?.length);
  if (!anySelected) {
    return initialTransformedGanttSelection;
  }

  //crossのtaskを取得
  const tasks = input.crosses ? input.crosses?.map((cross) => cross.task) : [];
  //dateにまたがるtaskを取得。date(yyyy-mm-dd)がTaskのstartとendの間にあるもの
  const dateMeasurementDate = input.date?.dataFile.measurementDate;
  if (dateMeasurementDate) {
    const dateTasks = allTasks.filter(
      (task) =>
        task.scheduledStartDate <= dateMeasurementDate &&
        dateMeasurementDate <= task.scheduledEndDate
    );
    tasks.push(...dateTasks);
  }

  //tasksの重複を削除
  const uniqueTasks = tasks.filter(
    (task, index, self) => self.findIndex((t) => t.id === task.id) === index
  );

  //設計データファイルを取得。uniqueTasksに含まれるtaskのdataFileIdと一致するもの
  const designDataFiles = allDataFiles.filter((dataFile) =>
    uniqueTasks.some(
      (task) =>
        task.dataFileId === dataFile.id &&
        dataFile.typeId === DataFileType.DESIGN &&
        dataFile.transferStatusCode === TransferStatusCode.SUCCESS
    )
  );
  //inputのdateとcrossから選択時間(selectedDate)が最も新しいものを取得。そのdataFileとtypeをasBuiltに格納
  const latestCrossSelectedDate =
    input.crosses?.reduce(
      (acc, cross) => (cross.selectedDateTime > acc ? cross.selectedDateTime : acc),
      0
    ) || 0;
  const latestDateSelectedDate = input.date?.selectedDateTime || 0;
  const selectedDateTime = Math.max(latestCrossSelectedDate, latestDateSelectedDate);

  const isCrossLarger = latestCrossSelectedDate > latestDateSelectedDate;
  const targetDataFile = isCrossLarger
    ? input.crosses?.find((cross) => cross.selectedDateTime === selectedDateTime)?.dataFile
    : input.date?.dataFile;

  if (!targetDataFile) {
    throw new Error('targetDataFile not found at transformGanttSelection');
  }

  const selectionType = isCrossLarger ? GanttSelectionType.cross : GanttSelectionType.date;
  //最もselectedDateTimeが新しいものを取得
  const latestCross = input.crosses?.length
    ? input.crosses.reduce(
        (acc, cross) => {
          if (cross.selectedDateTime > acc.selectedDateTime) {
            return cross;
          }
          return acc;
        },
        {
          task: {} as Task,
          dataFile: {} as DataFile,
          selectedDateTime: 0,
        }
      )
    : undefined;
  return {
    allCrossedTasks: uniqueTasks,
    designDataFiles,
    asBuiltDataFile: targetDataFile,
    selectionType,
    anySelected: true,
    task: latestCross?.task,
  };
};
